import { useRef, useState } from "react";
import { ChevronRight, FileText, Loader2, Plus, X } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../ui/collapsible";
import { CollabContextDocument } from "../../../models/audit/collabContextDocument";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";

interface ContextDocumentsSectionProps {
  documents?: CollabContextDocument[];
  onAdd: (files: File[]) => void;
  onRemove?: (documentId: string) => void;
  uploading?: boolean;
}

export const ContextDocumentsSection = ({
  documents,
  onAdd,
  onRemove,
  uploading = false,
}: ContextDocumentsSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const count = documents?.length ?? 0;
  const failedCount =
    documents?.filter((doc) => doc.state === "failed").length ?? 0;

  // Pending removal; drives the confirmation dialog.
  const [pendingRemoveDoc, setPendingRemoveDoc] =
    useState<CollabContextDocument | null>(null);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      onAdd(files);
    }
    // Reset so selecting the same file again re-triggers change.
    e.target.value = "";
  };

  return (
    <Collapsible
      defaultOpen
      className="flex-shrink-0 border-t border-stone-200 bg-white px-4 py-3"
    >
      <div className="flex items-center justify-between gap-2">
        <CollapsibleTrigger className="group flex items-center gap-1.5 rounded-sm text-xs font-semibold text-stone-700 hover:text-stone-900">
          <ChevronRight className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90" />
          Context Documents
          <span className="font-normal text-stone-400">({count})</span>
          {failedCount > 0 && (
            <span className="font-medium text-red-500">
              · {failedCount} failed
            </span>
          )}
        </CollapsibleTrigger>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Add Documents
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
      <CollapsibleContent>
        {/* Capped so a large set can never push the chat input out of the panel.
            The styled WebKit scrollbar stays visible (macOS otherwise overlays and
            hides it), so the box's extent reads at a glance and it can be dragged. */}
        <div className="mt-2 max-h-40 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-stone-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-stone-300 [&::-webkit-scrollbar-thumb:hover]:bg-stone-400">
          {documents && documents.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className={
                    doc.state === "failed"
                      ? "flex max-w-[220px] items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-500"
                      : "flex max-w-[220px] items-center gap-1.5 rounded-md border border-stone-200 bg-stone-50 px-2 py-1 text-xs text-stone-600"
                  }
                >
                  <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                  <span
                    className="truncate"
                    title={
                      doc.state === "failed"
                        ? `${doc.fileName} — processing failed; not usable as context`
                        : doc.fileName
                    }
                  >
                    {doc.fileName}
                  </span>
                  {doc.state === "failed" && (
                    <span className="flex-shrink-0 font-medium">failed</span>
                  )}
                  {onRemove && (
                    <button
                      type="button"
                      onClick={() => setPendingRemoveDoc(doc)}
                      className="flex-shrink-0 rounded-sm p-0.5 text-stone-400 hover:bg-stone-200 hover:text-stone-600"
                      aria-label={`Remove ${doc.fileName}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-stone-400">
              No context documents added yet.
            </p>
          )}
        </div>
      </CollapsibleContent>

      <ConfirmDeleteDialog
        open={pendingRemoveDoc !== null}
        onOpenChange={(o) => !o && setPendingRemoveDoc(null)}
        title="Remove context document?"
        description={
          pendingRemoveDoc
            ? `"${pendingRemoveDoc.fileName}" will be removed from the context documents.`
            : ""
        }
        onConfirm={() => {
          if (pendingRemoveDoc) {
            onRemove?.(pendingRemoveDoc.id);
          }
          setPendingRemoveDoc(null);
        }}
      />
    </Collapsible>
  );
};
